import React, { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import "./translations/i18n";
import urls from "./urls";
import ForgotPassword from "./pages/other/ForgotPassword";
import { Loader } from "./components/loader/Loader";
import moment from "moment";
import localization from "moment/locale/nl";
import CookieBanner from "./components/banner/CookieBanner";
import PrivateRoute from "./PrivateRoute";
import { colors } from "./utils/theme";
import { ConfigProvider, message, Result } from "antd";
import "dayjs/locale/nl";
import locale from "antd/locale/nl_NL";
import { auth } from "./services/firebase";
import ScrollToTop from "./components/global/scrollToTop";
import ErrorBoundaryFallback from "./components/core/ErrorBoundaryFallback";
import { ErrorBoundary } from "react-error-boundary";
import { sendExceptionToSlack } from "./services/api/exceptions";
import { getPostHogClient } from "./helpers/posthogManager";

moment.locale("nl", localization);

const Home = lazy(() => import("./pages/home/Home"));
// shop pages
const Dashboard = lazy(() => import("./pages/shop/Dashboard"));
const ShopListStandard = lazy(() => import("./pages/shop/ShopList"));
const CategoryLandingPage = lazy(() => import("./pages/shop/CategoryLanding"));

// product pages
const Product = lazy(() => import("./pages/shop-product/Product"));

// other pages
const About = lazy(() => import("./pages/other/About"));
const Contact = lazy(() => import("./pages/other/Contact"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const MyOrders = lazy(() => import("./pages/other/MyOrders"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));
const VerifyEmail = lazy(() => import("./pages/other/VerifyEmail"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const OrderConfirmed = lazy(() => import("./pages/other/OrderConfirmed"));
const OrderFailed = lazy(() => import("./pages/other/OrderFailed"));
const OrderProcessing = lazy(() => import("./pages/other/OrderProcessing"));
const PrivacyPolicy = lazy(() => import("./pages/other/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/other/TermsConditions"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

// Bombyxx Pages
const StoreLocator = lazy(() => import("./pages/bombyxx/StoreLocator"));
const B2BShowroom = lazy(() => import("./pages/bombyxx/Showroom"));
const GetInspired = lazy(() => import("./pages/bombyxx/GetInspired"));
const Vacatures = lazy(() => import("./pages/bombyxx/Vacatures"));

// Sales Pages
const SalesClients = lazy(() => import("./pages/sales/SalesClients"));
const ClientCart = lazy(() => import("./pages/sales/ClientCart"));
const QuoteEdit = lazy(() => import("./pages/sales/QuoteEdit"));
const QuoteSaved = lazy(() => import("./pages/sales/QuoteSaved"));

const clearFirestoreCache = () => {
  const map = globalThis["_reactFirePreloadedObservables"];
  Array.from(map.keys()).forEach(
    (key) => key.includes("firestore") && map.delete(key),
  );
};

const PageViewTracker = () => {
  const location = useLocation();
  const posthogClient = getPostHogClient();

  useEffect(() => {
    if (posthogClient) {
      posthogClient.capture("$pageview");
    }
  }, [location, posthogClient]);

  return null;
};

const App = () => {
  const [lastErrorLog, setLastErrorLog] = useState(null);

  useEffect(() => {
    // Clear firestore cache to prevent insufficient permissions bug
    const unsub = auth.beforeAuthStateChanged((user) => {
      clearFirestoreCache();
    });
    return () => unsub();
  }, []);

  const logError = (error) => {
    const now = Date.now();
    if (!lastErrorLog || now - lastErrorLog > 5000) {
      // 5 seconds cooldown
      const currentPage = window.location.pathname;
      sendExceptionToSlack(
        "Client crash op pagina " + currentPage + ": " + error.message,
      );
      setLastErrorLog(now);
    }
  };

  // Global guard for unhandled rejections and window errors (e.g., Firebase permission errors)
  useEffect(() => {
    const isPermissionError = (err) => {
      const msg = (err && (err.reason?.message || err.message)) || "";
      return (
        msg.includes("Missing or insufficient permissions") ||
        msg.includes("PERMISSION_DENIED") ||
        msg.includes("insufficient permissions")
      );
    };

    const onUnhandledRejection = (event) => {
      if (isPermissionError(event)) {
        event.preventDefault?.();
        message.error("Insufficient permissions to read this data.");
        logError(event.reason || event);
      }
    };

    const onWindowError = (event) => {
      if (isPermissionError(event.error || event)) {
        event.preventDefault?.();
        message.error("Insufficient permissions to read this data.");
        logError(event.error || event);
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onWindowError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onWindowError);
    };
  }, []);

  return (
    <>
      <ErrorBoundary
        fallbackRender={({ error }) => {
          const msg = error?.message || "";
          const isPerm =
            msg.includes("Missing or insufficient permissions") ||
            msg.includes("PERMISSION_DENIED") ||
            msg.includes("insufficient permissions") ||
            error?.code === "permission-denied";
          return isPerm ? (
            <div style={{ padding: 24 }}>
              <Result
                status="403"
                title="Insufficient permissions"
                subTitle="You do not have permission to view this content."
              />
            </div>
          ) : (
            <ErrorBoundaryFallback />
          );
        }}
        onError={logError}
      >
        <Router>
          <ScrollToTop />
          <PageViewTracker />
          <ConfigProvider
            locale={locale}
            theme={{
              token: {
                colorPrimary: colors.cta_btn,
                fontFamily: "",
              },
              components: {
                Button: {
                  colorPrimary: colors.cta_btn,
                  colorBorder: colors.cta_btn,
                  colorTextLightSolid: "#fff",
                  colorText: colors.primary,
                  defaultBg: colors.primary,
                  defaultBorderColor: colors.primary,
                  defaultColor: "#fff",
                  defaultActiveBg: colors.primary80,
                  defaultActiveBorderColor: colors.primary80,
                  defaultActiveColor: "#fff",
                  defaultHoverBg: colors.primary90,
                  defaultHoverBorderColor: colors.primary90,
                  defaultHoverColor: "#fff",
                  defaultShadow: "none",
                  primaryShadow: "none",
                  borderColorDisabled: "#d9d9d9",
                  borderRadiusSM: 2,
                  borderRadius: 2,
                  borderRadiusLG: 2,
                },
              },
            }}
          >
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Homepage */}
                <Route path={urls.home} element={<Home />} />

                {/* Shop pages */}
                <Route
                  path={urls.shopLandingPage}
                  element={<PrivateRoute element={<Dashboard />} />}
                />
                <Route
                  path={urls.shop}
                  element={<PrivateRoute element={<ShopListStandard />} />}
                />
                <Route
                  path={urls.productsByCategory}
                  element={<PrivateRoute element={<ShopListStandard />} />}
                />

                {/* Shop product pages */}
                <Route
                  path={urls.productDetail}
                  element={<PrivateRoute element={<Product />} />}
                />

                {/* Category landing page */}
                <Route
                  path={urls.categoryLandingPage}
                  element={<PrivateRoute element={<CategoryLandingPage />} />}
                />
                <Route
                  path={urls.subcategoryLandingPage}
                  element={<CategoryLandingPage />}
                />

                {/* Other pages */}
                <Route path={urls.about} element={<About />} />
                <Route path={urls.contact} element={<Contact />} />
                <Route
                  path={urls.my_account}
                  element={<PrivateRoute element={<MyAccount />} />}
                />
                <Route
                  path={urls.my_orders}
                  element={<PrivateRoute element={<MyOrders />} />}
                />
                <Route path={urls.login_register} element={<LoginRegister />} />
                <Route
                  path={urls.forgot_password}
                  element={<ForgotPassword />}
                />
                <Route path={urls.email_verified} element={<VerifyEmail />} />
                <Route path={urls.privacy} element={<PrivacyPolicy />} />

                <Route
                  path={urls.cart}
                  element={<PrivateRoute element={<Cart />} />}
                />
                <Route
                  path={urls.wishlist}
                  element={<PrivateRoute element={<Wishlist />} />}
                />
                <Route
                  path={urls.checkout}
                  element={<PrivateRoute element={<Checkout />} />}
                />
                <Route
                  path={urls.order_confirmed}
                  element={<PrivateRoute element={<OrderConfirmed />} />}
                />
                <Route
                  path={urls.order_failed}
                  element={<PrivateRoute element={<OrderFailed />} />}
                />
                <Route
                  path={urls.order_processing + urls.document_id_param}
                  element={<PrivateRoute element={<OrderProcessing />} />}
                />
                <Route path={urls.not_found} element={<NotFound />} />
                <Route
                  path={urls.terms_conditions}
                  element={<TermsConditions />}
                />

                <Route path={urls.store_locator} element={<StoreLocator />} />
                <Route path={urls.b2b_showroom} element={<B2BShowroom />} />
                <Route path={urls.get_inspired} element={<GetInspired />} />
                <Route path={urls.vacancies} element={<Vacatures />} />

                {/* Sales routes */}
                <Route
                  path={urls.sales_clients}
                  element={<PrivateRoute element={<SalesClients />} />}
                />
                <Route
                  path={`${urls.client_cart}/:clientId`}
                  element={<PrivateRoute element={<ClientCart />} />}
                />
                <Route
                  path={`${urls.quote_edit}/:quoteId`}
                  element={<PrivateRoute element={<QuoteEdit />} />}
                />
                <Route
                  path={urls.quote_saved}
                  element={<PrivateRoute element={<QuoteSaved />} />}
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ConfigProvider>
          <CookieBanner />
        </Router>
      </ErrorBoundary>
    </>
  );
};

export default App;
